// =========================================================================
// The signing link the sheet shows — on the signing site fixed in THIS build.
//
// The server used to choose the whole link, and the sheet drew it as the QR
// code with no check of where it pointed (review of 4 October, R6). A server
// that has been taken over could send the operator to a page that is not ours,
// or to our page on an origin where the device's own list of confirmed
// destinations is empty. Every protection on the signing page is skipped by
// never opening it.
//
// So the server's link is taken apart, and only one thing is kept from it: the
// token. The origin comes from this build, the path from what the operator
// asked for. Anything else in the link — another site, another transaction,
// extra parts — is a refusal.
//
// Pure, so it has tests. No default origin: a build that does not name its
// signing site shows no link, rather than a guess.
// =========================================================================

declare const REBUILT: unique symbol;

/**
 * A signing link that signingLink() rebuilt. The sheet draws nothing else: its
 * props ask for this type, so handing it the server's own string does not
 * compile.
 */
export type SigningUrl = string & { readonly [REBUILT]: true };

/** What the operator asked a link for. */
export type LinkSubject =
  | { kind: 'approval'; txid: string }
  | { kind: 'whitelist'; id: number };

/**
 * The capability token: three dot-separated runs of URL-safe characters. A real
 * one is about 115 characters; the bound keeps a server from sending one too
 * large for the QR code to be drawn.
 */
const TOKEN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const MAX_TOKEN_LENGTH = 512;

/**
 * The path on the signing site for what was asked — or null when the thing
 * asked about is not shaped like a transaction id or an entry id.
 *
 * Those ids come from the server's own lists. An "id" of `whitelist/7` would
 * otherwise turn a transaction's sheet into a link to the address-approval
 * page, and `7/<txid>` the reverse.
 */
function pathFor(subject: LinkSubject): string | null {
  if (subject.kind === 'approval') {
    return typeof subject.txid === 'string' && /^[0-9a-fA-F]{64}$/.test(subject.txid)
      ? `/${subject.txid.toLowerCase()}`
      : null;
  }
  return Number.isSafeInteger(subject.id) && subject.id > 0 ? `/whitelist/${subject.id}` : null;
}

const refusal = (why: string) => new Error(`${why} No signing link is shown. Report this.`);

/**
 * The signing site this build was made for, or an Error saying it names none.
 *
 * Called BEFORE the server is asked for a link: a build that cannot show a
 * link should not have a live token minted, and an audit row written, for
 * every attempt.
 */
export function signingSite(origin: string | undefined): URL {
  let site: URL;
  try {
    site = new URL(origin ?? '');
  } catch {
    throw refusal('This build of the app does not name its signing page.');
  }
  // An origin and nothing more: a path or a query here would be dropped
  // silently, and the build would not mean what its setting says.
  if (`${site.origin}/` !== site.href) {
    throw refusal('This build of the app names its signing page wrongly.');
  }
  return site;
}

/**
 * The link to show, rebuilt from parts this app trusts — or an Error whose
 * message says why there is none.
 *
 * @param serverUrl  the link the server returned
 * @param origin     the signing site this build was made for (VITE_COSIGN_ORIGIN)
 * @param subject    what the operator asked for
 */
export function signingLink(serverUrl: string, origin: string | undefined, subject: LinkSubject): SigningUrl {
  const site = signingSite(origin);

  const path = pathFor(subject);
  if (path === null) {
    throw refusal('This app was given something that is not a transaction or an address entry to open.');
  }

  let link: URL;
  try {
    link = new URL(serverUrl);
  } catch {
    throw refusal('The server sent a signing link that is not a link.');
  }
  if (link.origin !== site.origin) {
    throw refusal(`The server sent a signing link that does not point at this app's signing page (it points at ${link.protocol}//${link.host}).`);
  }
  if (link.username !== '' || link.password !== '') {
    throw refusal('The server sent a signing link that carries a user name, which a signing link never does.');
  }
  if (link.pathname.toLowerCase() !== path || link.search !== '') {
    throw refusal('The server sent a signing link for something other than what you opened.');
  }
  const token = link.hash.startsWith('#t=') ? link.hash.slice(3) : '';
  if (token.length > MAX_TOKEN_LENGTH || !TOKEN.test(token)) {
    throw refusal('The server sent a signing link without a usable token.');
  }

  return `${site.origin}${path}#t=${token}` as SigningUrl;
}

/**
 * Ask the server for a link and hand back the one to show.
 *
 * The order is the point, so it lives here where it has tests: the build's
 * signing site and the thing asked about are checked BEFORE the server is
 * asked. A build that names no site used to have a live token minted, and a
 * "link minted" row written to the audit trail, for every link it then refused
 * to show.
 *
 * Only `expiresAt` and the rebuilt link come back — nothing else the server
 * put in its answer.
 */
export async function mintedLink(
  origin: string | undefined,
  subject: LinkSubject,
  askServer: () => Promise<{ url: string; expiresAt: string }>,
): Promise<{ url: SigningUrl; expiresAt: string }> {
  signingSite(origin);
  if (pathFor(subject) === null) {
    throw refusal('This app was given something that is not a transaction or an address entry to open.');
  }
  const minted = await askServer();
  return { url: signingLink(minted.url, origin, subject), expiresAt: minted.expiresAt };
}

